import { pgTable, uuid, text, varchar, integer, real, boolean, timestamp, jsonb, index, uniqueIndex, pgEnum, vector, } from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
// ── Enums ──────────────────────────────────────────────────────────────
export const workAuthTagEnum = pgEnum("work_auth_tag", [
    "remote-from-india",
    "relocation-sponsored",
    "oci-friendly",
    "h1b-transfer",
    "india-onsite",
    "hybrid-india",
    "contract-remote",
]);
export const jobSourceEnum = pgEnum("job_source", [
    "extension-naukri",
    "extension-linkedin",
    "extension-indeed",
    "extension-iimjobs",
    "vps-career-page",
    "vps-remote-portals",
    "vps-greenhouse",
    "vps-ashby",
    "vps-lever",
    "vps-naukri",
    "vps-linkedin",
    "vps-monster",
    "vps-search-api",
]);
export const applicationStatusEnum = pgEnum("application_status", [
    "auto-applied",
    "pending-review",
    "applied",
    "interviewing",
    "offer",
    "rejected",
    "blocked",
    "saved",
    "skipped",
]);
export const portalTrustEnum = pgEnum("portal_trust", ["high", "medium", "low"]);
// ── Structured job geography (Stage 2) ──────────────────────────────────────
// Work mode and remote SCOPE are deliberately separate enums: "remote" says how
// the work is done, the scope says from where it may be done. Collapsing them is
// what made "Remote — US only" indistinguishable from "Remote" (see
// docs/GENERAL_MATCHER_GEOGRAPHY_STAGE2.md). "unknown" is a stored value, never
// NULL-as-unknown, so "not yet normalized" stays distinguishable.
export const jobWorkModeEnum = pgEnum("job_work_mode", ["remote", "hybrid", "onsite", "unknown"]);
export const jobRemoteScopeEnum = pgEnum("job_remote_scope", [
    "global", "country_restricted", "region_restricted", "unspecified", "unknown",
]);
export const geoConfidenceEnum = pgEnum("geo_confidence", ["high", "medium", "unknown"]);
export const userSegmentEnum = pgEnum("user_segment", [
    "india-global-remote",
    "return-to-india",
    "senior-non-it",
    "freshly-returned",
    "diaspora",
    "senior-freelancer",
]);
// ── Users ──────────────────────────────────────────────────────────────
export const users = pgTable("users", {
    id: uuid("id").primaryKey().defaultRandom(),
    email: varchar("email", { length: 255 }).notNull().unique(),
    passwordHash: text("password_hash"),
    googleId: varchar("google_id", { length: 255 }).unique(),
    name: varchar("name", { length: 255 }).notNull(),
    avatarUrl: text("avatar_url"),
    segment: userSegmentEnum("segment").default("india-global-remote"),
    emailVerified: boolean("email_verified").default(false).notNull(),
    verificationToken: varchar("verification_token", { length: 64 }),
    // Password-reset (forgot-password): single-use, expiring token. Null when no
    // reset is in flight. Cleared on a successful reset. See migration 0012.
    resetToken: varchar("reset_token", { length: 64 }),
    resetTokenExpiry: timestamp("reset_token_expiry"),
    // Paid deep-score credits balance (Razorpay credit-pack purchases top this up;
    // spending a deep analysis decrements it). Separate from the 1 free preview.
    deepScoreCredits: integer("deep_score_credits").default(0).notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
// ── Resumes ────────────────────────────────────────────────────────────
export const resumes = pgTable("resumes", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    rawText: text("raw_text").notNull(),
    structuredJson: jsonb("structured_json"), // Claude-parsed structure
    embedding: vector("embedding", { dimensions: 1536 }), // text-embedding-3-small
    targetRoles: text("target_roles").array(),
    targetLocations: text("target_locations").array(),
    salaryMinLpa: integer("salary_min_lpa"),
    salaryMaxLpa: integer("salary_max_lpa"),
    workAuthTags: workAuthTagEnum("work_auth_tags").array(),
    seniorityLevel: varchar("seniority_level", { length: 50 }),
    // Provenance of the résumé row (Option B thin-upgrade). NULL = legacy/unknown.
    //   user_upload     — a genuine native self-upload (never auto-overwritten)
    //   partner_form    — a THIN structured partner résumé (upgradeable by Option B)
    //   partner_resume  — a real parsed partner résumé (Option B done — do not re-parse)
    // Lets the partner path upgrade a thin résumé to the parsed one exactly once,
    // then stay idempotent, without ever clobbering a real user upload. Additive.
    source: varchar("source", { length: 30 }),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
    userIdx: index("resumes_user_idx").on(t.userId),
}));
// ── Jobs ───────────────────────────────────────────────────────────────
export const jobs = pgTable("jobs", {
    id: uuid("id").primaryKey().defaultRandom(),
    // Content
    title: varchar("title", { length: 500 }).notNull(),
    company: varchar("company", { length: 255 }).notNull(),
    location: varchar("location", { length: 255 }),
    description: text("description"),
    salaryRaw: varchar("salary_raw", { length: 100 }),
    salaryMinLpa: integer("salary_min_lpa"),
    salaryMaxLpa: integer("salary_max_lpa"),
    // Source
    url: text("url").notNull(),
    source: jobSourceEnum("source").notNull(),
    workAuthTag: workAuthTagEnum("work_auth_tag"),
    portalTrust: portalTrustEnum("portal_trust").default("medium"),
    // Admission gate (8d): why the ingest gate admitted this job. NULL = accepted
    // clean (or gate off — legacy rows). Non-null → "needs_review" reason (the job
    // entered the pool FLAGGED so its family stays matchable). Additive, nullable.
    admissionReason: varchar("admission_reason", { length: 100 }),
    // Dedup
    jdHash: varchar("jd_hash", { length: 64 }).notNull(),
    repostCount: integer("repost_count").default(0).notNull(),
    // Embedding for semantic search
    embedding: vector("embedding", { dimensions: 1536 }),
    // Freshness
    postedAt: timestamp("posted_at"),
    firstSeenAt: timestamp("first_seen_at").defaultNow().notNull(),
    lastSeenAt: timestamp("last_seen_at").defaultNow().notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    // ── Structured geography (Stage 2, additive + nullable) ───────────────────
    // `location` above stays the raw provenance string and is never rewritten.
    // NULL geoNormalizedAt = never normalized; an enum value of "unknown" =
    // normalized and genuinely unknown. Per-location facts live in job_locations.
    geoWorkMode: jobWorkModeEnum("geo_work_mode"),
    geoRemoteScope: jobRemoteScopeEnum("geo_remote_scope"),
    geoRemoteCountries: text("geo_remote_countries").array(),
    geoConfidence: geoConfidenceEnum("geo_confidence"),
    geoNormalizedAt: timestamp("geo_normalized_at"),
    geoNormalizerVersion: integer("geo_normalizer_version"),
}, (t) => ({
    urlIdx: uniqueIndex("jobs_url_idx").on(t.url),
    hashIdx: index("jobs_hash_idx").on(t.jdHash),
    activeIdx: index("jobs_active_idx").on(t.isActive),
    companyIdx: index("jobs_company_idx").on(t.company),
    sourceIdx: index("jobs_source_idx").on(t.source),
}));
// ── Matches ────────────────────────────────────────────────────────────
export const matches = pgTable("matches", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    jobId: uuid("job_id").notNull().references(() => jobs.id, { onDelete: "cascade" }),
    resumeId: uuid("resume_id").notNull().references(() => resumes.id, { onDelete: "cascade" }),
    // Scoring stages
    stage1Pass: boolean("stage1_pass").default(false).notNull(),
    semanticScore: real("semantic_score"), // cosine similarity 0-1
    weightedScore: real("weighted_score"), // 0-100
    confidence: real("confidence"), // 0-1
    // Breakdown (30% title, 25% skills, 20% seniority, 10% industry, 10% company, 5% work mode)
    scoreBreakdown: jsonb("score_breakdown"),
    // AI explanation
    explanation: text("explanation"),
    autoApplyEligible: boolean("auto_apply_eligible").default(false).notNull(),
    // Scoring metadata
    scoredAt: timestamp("scored_at").defaultNow().notNull(),
    modelUsed: varchar("model_used", { length: 100 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    // Soft-delete for safe per-user refresh — preserves application history
    isStale: boolean("is_stale").default(false).notNull(),
    staleReason: varchar("stale_reason", { length: 100 }),
}, (t) => ({
    userJobIdx: uniqueIndex("matches_user_job_idx").on(t.userId, t.jobId),
    userScoreIdx: index("matches_user_score_idx").on(t.userId, t.weightedScore),
    autoApplyIdx: index("matches_auto_apply_idx").on(t.autoApplyEligible, t.userId),
    staleIdx: index("matches_stale_idx").on(t.userId, t.isStale),
}));
// ── Free LLM preview grants ────────────────────────────────────────────
// One row per free full-LLM "See full analysis" preview a user spends (the
// upgrade hook). The per-user COUNT of rows here is the hard cap
// (FREE_LLM_PREVIEW_COUNT). Additive + fully reversible (DROP TABLE reverts).
// `costUsd` records the estimated spend for observability. No prompt/payload —
// ids + cost only. A unique (userId, matchId) prevents double-previewing a match.
export const llmPreviewGrants = pgTable("llm_preview_grants", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    jobId: uuid("job_id").notNull().references(() => jobs.id, { onDelete: "cascade" }),
    // Informational back-ref ONLY. NULLABLE + ON DELETE SET NULL: re-matching
    // deletes+reinserts a user's match rows (new ids), so the grant must NOT be
    // wiped when its match is deleted — else the free-preview cap resets and can be
    // farmed by re-uploading. The DURABLE identity is (user_id, job_id) below.
    matchId: uuid("match_id").references(() => matches.id, { onDelete: "set null" }),
    usedAt: timestamp("used_at").defaultNow().notNull(),
    costUsd: real("cost_usd"),
}, (t) => ({
    userIdx: index("llm_preview_grants_user_idx").on(t.userId),
    // Cap key is (user, JOB) — stable across re-matching. One grant per user per job.
    userJobIdx: uniqueIndex("llm_preview_grants_user_job_idx").on(t.userId, t.jobId),
}));
// ── Premium evaluation runs (Phase 12 hardening) ───────────────────────
// Durable run + quota-reservation state for Premium LLM evaluation, so the
// Anthropic call can happen OUTSIDE any transaction while still being
// idempotent, bounded and recoverable. See migration 0015 for the full rationale
// and pilot/premium-run.ts for the lifecycle that uses it.
//
// Identity is (userId, evaluationKey) where evaluationKey is content-derived
// (pilot/premium-evaluation-key.ts) — NOT matchId. Migration 0010 re-keyed
// llm_preview_grants off matchId because re-matching deletes+reinserts match rows
// with new ids, which reset the cap and allowed farming unlimited paid calls; the
// same hazard applies here, so matchId is an informational back-ref only.
export const premiumEvaluationRuns = pgTable("premium_evaluation_runs", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    jobId: uuid("job_id").notNull().references(() => jobs.id, { onDelete: "cascade" }),
    matchId: uuid("match_id").references(() => matches.id, { onDelete: "set null" }),
    // sha256 of (promptVersion, userId, jobId, resumeId, resume.updatedAt,
    // jobContentHash, model). A change to ANY input yields a new key → cache miss.
    evaluationKey: varchar("evaluation_key", { length: 64 }).notNull(),
    promptVersion: varchar("prompt_version", { length: 32 }).notNull(),
    model: varchar("model", { length: 100 }).notNull(),
    // reserved | succeeded | failed
    status: varchar("status", { length: 20 }).default("reserved").notNull(),
    failureReason: varchar("failure_reason", { length: 120 }),
    // Bounded-attempt accounting — the ceiling is what makes "provider ran but
    // persistence failed" bounded instead of an unlimited unpaid-repeat loop.
    attemptCount: integer("attempt_count").default(0).notNull(),
    creditsReserved: integer("credits_reserved").default(0).notNull(),
    creditsConsumed: integer("credits_consumed").default(0).notNull(),
    reservedAt: timestamp("reserved_at").defaultNow().notNull(),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
    // THE idempotency key — concurrent duplicate requests race on this unique
    // index so exactly one wins the reservation and issues one provider call.
    userKeyIdx: uniqueIndex("premium_eval_runs_user_key_idx").on(t.userId, t.evaluationKey),
    statusIdx: index("premium_eval_runs_status_idx").on(t.status, t.reservedAt),
    userIdx: index("premium_eval_runs_user_idx").on(t.userId),
}));
// ── Payments (Razorpay credit-pack purchases) ──────────────────────────
// One row per Razorpay order. amount is server-authoritative (paise, from the
// server plan catalog — never the client). razorpay_payment_id is UNIQUE so a
// captured payment grants its credits exactly once (idempotency). Additive.
export const payments = pgTable("payments", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    planId: varchar("plan_id", { length: 40 }).notNull(),
    credits: integer("credits").notNull(), // credits this purchase grants
    amount: integer("amount").notNull(), // paise (server-authoritative)
    currency: varchar("currency", { length: 8 }).default("INR").notNull(),
    razorpayOrderId: varchar("razorpay_order_id", { length: 64 }).notNull().unique(),
    razorpayPaymentId: varchar("razorpay_payment_id", { length: 64 }).unique(), // set on capture
    status: varchar("status", { length: 20 }).default("created").notNull(), // created|paid|failed
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
    userIdx: index("payments_user_idx").on(t.userId),
}));
// ── Applications ───────────────────────────────────────────────────────
export const applications = pgTable("applications", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    jobId: uuid("job_id").notNull().references(() => jobs.id, { onDelete: "cascade" }),
    matchId: uuid("match_id").references(() => matches.id),
    status: applicationStatusEnum("status").default("pending-review").notNull(),
    appliedAt: timestamp("applied_at"),
    submissionId: varchar("submission_id", { length: 255 }),
    retryCount: integer("retry_count").default(0).notNull(),
    auditLog: jsonb("audit_log").default(sql `'[]'::jsonb`).notNull(),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
    userJobIdx: uniqueIndex("applications_user_job_idx").on(t.userId, t.jobId),
    userStatusIdx: index("applications_user_status_idx").on(t.userId, t.status),
}));
// ── User-captured jobs (r3, Chrome extension) ─────────────────────────
// PRIVATE by construction (owner decision 1): a job a user captures with the
// extension lives ONLY here — never in `jobs`, so no matching / browse / partner /
// admin-pool / freshness query can see it (none of them names this table). Each
// row is also the user's tracker entry (same status vocabulary as applications).
// Job content only — no personal data from the page (owner decision 6).
export const userCapturedJobs = pgTable("user_captured_jobs", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    title: varchar("title", { length: 300 }).notNull(),
    company: varchar("company", { length: 200 }).notNull(),
    location: varchar("location", { length: 200 }),
    description: text("description").notNull(),
    url: text("url").notNull(), // canonical: origin + path + job-id params only
    urlHash: varchar("url_hash", { length: 64 }).notNull(),
    sourceSite: varchar("source_site", { length: 100 }).notNull(),
    matchScore: integer("match_score"), // V1 quick score 0–100; NULL when not scored
    scoreStatus: varchar("score_status", { length: 30 }).notNull(), // scored | no_resume | no_embedding | embedding_failed
    scoreBreakdown: jsonb("score_breakdown"),
    scoredResumeId: uuid("scored_resume_id"),
    status: applicationStatusEnum("status").default("saved").notNull(),
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
    userUrlIdx: uniqueIndex("user_captured_jobs_user_url_idx").on(t.userId, t.urlHash),
    userCreatedIdx: index("user_captured_jobs_user_created_idx").on(t.userId, t.createdAt),
}));
// ── User consents (v1.1, SIGNUP_CONSENT_REQUIRED) ─────────────────────
// One row per consent a user gave (kind + version of the DRAFT legal text). Only written
// when the flag is on; a separate table so the users row and every query on it are
// unchanged. Cascades with the account.
export const userConsents = pgTable("user_consents", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    kind: varchar("kind", { length: 40 }).notNull(), // signup_terms_privacy | google_signup_notice
    version: varchar("version", { length: 40 }).notNull(),
    acceptedAt: timestamp("accepted_at").defaultNow().notNull(),
}, (t) => ({
    userIdx: index("user_consents_user_idx").on(t.userId),
}));
// ── Portals ────────────────────────────────────────────────────────────
export const portals = pgTable("portals", {
    id: uuid("id").primaryKey().defaultRandom(),
    name: varchar("name", { length: 255 }).notNull().unique(),
    baseUrl: text("base_url").notNull(),
    apiUrl: text("api_url"),
    trustLevel: portalTrustEnum("trust_level").default("medium").notNull(),
    isEnabled: boolean("is_enabled").default(true).notNull(),
    scrapeType: varchar("scrape_type", { length: 50 }), // 'api' | 'playwright' | 'extension'
    config: jsonb("config"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export const scanJobRuns = pgTable("scan_job_runs", {
    id: uuid("id").primaryKey().defaultRandom(),
    startedAt: timestamp("started_at").notNull(),
    finishedAt: timestamp("finished_at").notNull(),
    scannedPortals: integer("scanned_portals").notNull(),
    totalFetched: integer("total_fetched").notNull(),
    totalInserted: integer("total_inserted").notNull(),
    totalSkipped: integer("total_skipped").notNull(),
    totalErrors: integer("total_errors").notNull(),
    sourceSummary: jsonb("source_summary").notNull(),
    status: varchar("status", { length: 32 }).notNull(),
    errorMessage: text("error_message"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
    startedIdx: index("scan_job_runs_started_idx").on(t.startedAt),
    createdIdx: index("scan_job_runs_created_idx").on(t.createdAt),
}));
// ── Refresh Tokens ─────────────────────────────────────────────────────
export const refreshTokens = pgTable("refresh_tokens", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    token: varchar("token", { length: 512 }).notNull().unique(),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
    tokenIdx: index("refresh_tokens_token_idx").on(t.token),
    userIdx: index("refresh_tokens_user_idx").on(t.userId),
}));
// ── Admin user preferences ─────────────────────────────────────────────
export const adminUserPreferences = pgTable("admin_user_preferences", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().unique().references(() => users.id, { onDelete: "cascade" }),
    adminNotes: text("admin_notes"),
    userMatchingEnabled: boolean("user_matching_enabled").default(true).notNull(),
    resumeMatchingEnabled: boolean("resume_matching_enabled").default(true).notNull(),
    disabledReason: text("disabled_reason"),
    disabledAt: timestamp("disabled_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
    userIdx: index("admin_user_prefs_user_idx").on(t.userId),
}));
// ── Partner candidate mappings ─────────────────────────────────────────
// Links an external partner's candidate (e.g. ExH1B profiles.id) to a
// Caliber user. Enables partner auto-provisioning without exposing partner
// internals into the first-party users table. Additive only — no first-party
// auth/matching/scan behaviour depends on this table.
export const partnerCandidateMappings = pgTable("partner_candidate_mappings", {
    id: uuid("id").primaryKey().defaultRandom(),
    partner: text("partner").notNull(), // e.g. 'exh1b'
    partnerCandidateId: text("partner_candidate_id").notNull(), // partner's candidate id (ExH1B profiles.id)
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    source: text("source").notNull().default("exh1b"), // attribution shown in admin
    createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
    partnerCandidateUnique: uniqueIndex("partner_candidate_mappings_partner_candidate_unique").on(t.partner, t.partnerCandidateId),
    userIdx: index("partner_candidate_mappings_user_idx").on(t.userId),
}));
// ── Partner registry (multi-client token foundation) ───────────────────
// Additive. Not consumed at runtime until PARTNER_TOKEN_REGISTRY_ENABLED=true;
// until then the env EXH1B_PARTNER_TOKEN path remains authoritative.
export const partners = pgTable("partners", {
    id: uuid("id").primaryKey().defaultRandom(),
    partnerKey: text("partner_key").notNull().unique(), // e.g. 'exh1b'
    displayName: text("display_name").notNull(),
    status: text("status").notNull().default("active"), // active | disabled
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export const partnerApiTokens = pgTable("partner_api_tokens", {
    id: uuid("id").primaryKey().defaultRandom(),
    partnerId: uuid("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
    tokenHash: text("token_hash").notNull().unique(), // sha256 hex — never plaintext
    tokenPrefix: text("token_prefix").notNull(), // for identification only (not secret)
    status: text("status").notNull().default("active"), // active | revoked
    createdAt: timestamp("created_at").defaultNow().notNull(),
    rotatedAt: timestamp("rotated_at"),
    revokedAt: timestamp("revoked_at"),
    expiresAt: timestamp("expires_at"),
}, (t) => ({
    tokenHashIdx: uniqueIndex("partner_api_tokens_hash_idx").on(t.tokenHash),
    partnerIdx: index("partner_api_tokens_partner_idx").on(t.partnerId),
}));
// ── Partner provider configuration (BYOK) ───────────────────────────────
// Additive. Metadata/reference only — NEVER a raw provider secret column.
// `secretRef` is an opaque identifier resolved through the server-side secret
// store (packages/api/src/partner/secret-store.ts), not a credential itself.
// Not consumed at runtime until the PR2 provider-resolution wiring lands; a
// partner with no row (or a disabled row) here keeps using Caliber's default/
// global provider credentials exactly as today.
export const partnerProviderConfigs = pgTable("partner_provider_configs", {
    id: uuid("id").primaryKey().defaultRandom(),
    partnerId: uuid("partner_id").notNull().references(() => partners.id, { onDelete: "cascade" }),
    capability: text("capability").notNull(), // 'embedding' | 'resume_parsing'
    provider: text("provider").notNull(), // 'openai' | 'anthropic'
    model: text("model"), // provider-specific model name, when applicable
    secretRef: text("secret_ref").notNull(), // opaque identifier — never the credential itself
    enabled: boolean("enabled").notNull().default(true),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
    partnerCapabilityUnique: uniqueIndex("partner_provider_configs_partner_capability_idx").on(t.partnerId, t.capability),
}));
// Partner-sourced candidate job PREFERENCES — stored SEPARATELY from resume
// content (never mixed into resumes.structured_json). One row per Caliber user.
// The `preferences` jsonb holds the full normalized preference object (target
// country/location, work mode, comp, job type, titles, seniority, industries,
// work authorization, availability, preferred/excluded companies, skills,
// timezones, shift, travel). Used for ranking/filtering; never for auth.
export const partnerCandidatePreferences = pgTable("partner_candidate_preferences", {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    partner: text("partner").notNull().default("exh1b"),
    preferences: jsonb("preferences").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
    userUnique: uniqueIndex("partner_candidate_preferences_user_unique").on(t.userId),
}));
// One row per physical location a job is offered in. A single-location job has
// exactly one row; a remote job with no place has none. `position` preserves the
// posting's own order and makes the delete-then-insert backfill idempotent.
export const jobLocations = pgTable("job_locations", {
    id: uuid("id").primaryKey().defaultRandom(),
    jobId: uuid("job_id").notNull().references(() => jobs.id, { onDelete: "cascade" }),
    position: integer("position").notNull().default(0),
    countryCode: varchar("country_code", { length: 2 }),
    regionName: varchar("region_name", { length: 120 }),
    regionCode: varchar("region_code", { length: 10 }),
    cityName: varchar("city_name", { length: 120 }),
    cityKey: varchar("city_key", { length: 120 }),
    confidence: geoConfidenceEnum("confidence").notNull().default("unknown"),
    rawFragment: varchar("raw_fragment", { length: 255 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
    jobPositionUnique: uniqueIndex("job_locations_job_position_unique").on(t.jobId, t.position),
    byJob: index("job_locations_job_idx").on(t.jobId),
    byCountry: index("job_locations_country_idx").on(t.countryCode),
    byCityKey: index("job_locations_city_key_idx").on(t.cityKey),
}));
// ── Relations ──────────────────────────────────────────────────────────
export const usersRelations = relations(users, ({ many, one }) => ({
    resumes: many(resumes),
    matches: many(matches),
    applications: many(applications),
    refreshTokens: many(refreshTokens),
    adminPreferences: one(adminUserPreferences, { fields: [users.id], references: [adminUserPreferences.userId] }),
}));
export const resumesRelations = relations(resumes, ({ one, many }) => ({
    user: one(users, { fields: [resumes.userId], references: [users.id] }),
    matches: many(matches),
}));
export const jobsRelations = relations(jobs, ({ many }) => ({
    matches: many(matches),
    applications: many(applications),
}));
export const matchesRelations = relations(matches, ({ one }) => ({
    user: one(users, { fields: [matches.userId], references: [users.id] }),
    job: one(jobs, { fields: [matches.jobId], references: [jobs.id] }),
    resume: one(resumes, { fields: [matches.resumeId], references: [resumes.id] }),
}));
export const applicationsRelations = relations(applications, ({ one }) => ({
    user: one(users, { fields: [applications.userId], references: [users.id] }),
    job: one(jobs, { fields: [applications.jobId], references: [jobs.id] }),
    match: one(matches, { fields: [applications.matchId], references: [matches.id] }),
}));
export const adminUserPreferencesRelations = relations(adminUserPreferences, ({ one }) => ({
    user: one(users, { fields: [adminUserPreferences.userId], references: [users.id] }),
}));
//# sourceMappingURL=schema.js.map